import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  Flame, Calendar, TrendingUp, AlertCircle, ChevronRight, Loader2, Clock,
  Video, MapPin, MessageSquare, Phone, Mail, StickyNote,
} from 'lucide-react';

const CAT_LABELS = {
  agence: 'Agences', syndic: 'Syndics', architecte: 'Architectes', mairie: 'Mairies', autre: 'Autres',
};

const TYPE_ICONS = {
  conversation: MessageSquare, appel: Phone, rdv: Calendar, visite: MapPin, email: Mail, note: StickyNote,
};

/**
 * Bandeau compact — un seul bloc discret (fond slate) qui regroupe :
 *  - 4 mini-KPI horizontaux
 *  - Prochains RDV + Activité récente en accordéon (ouvert par défaut si contenu, sinon fermé)
 *  - Relances dues en pastille inline
 * Palette volontairement neutre (slate/blanc) pour ne pas concurrencer les 4 tuiles catégories.
 */
export default function ProspectionsDashboard({ onOpenProspect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/prospects/dashboard');
        setData(r.data);
      } catch (_) { /* noop */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="w-4 h-4 animate-spin" /> Chargement du pilotage commercial…
    </div>
  );
  if (!data) return null;

  const { kpis, upcoming_rdvs = [], due_relances = [], recent_activities = [] } = data;
  const hasDetails = upcoming_rdvs.length + due_relances.length + recent_activities.length > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="prospections-dashboard">
      {/* Bandeau compact — inline KPI row */}
      <div className="px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-100">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mr-1">Pilotage commercial</span>
        <MiniKpi icon={Flame}      label="Chauds"          value={kpis.hot_total}      tone="text-orange-600"  testid="kpi-hot" />
        <MiniKpi icon={Calendar}   label="RDV 7 j."        value={kpis.rdv_this_week}  tone="text-emerald-600" testid="kpi-rdv-week" />
        <MiniKpi icon={TrendingUp} label="Signés 30 j."    value={kpis.converted_30d}  tone="text-blue-600"    testid="kpi-converted" />
        <MiniKpi icon={AlertCircle} label="Relances dues"  value={kpis.relances_due}   tone="text-amber-600"   testid="kpi-relances" />
        <div className="ml-auto">
          {hasDetails ? (
            <button
              onClick={() => setShowDetails(s => !s)}
              className="text-xs text-slate-600 hover:text-slate-900 font-medium inline-flex items-center gap-1"
              data-testid="dashboard-toggle-details"
            >
              {showDetails ? 'Masquer' : 'Voir'} le détail
              <ChevronRight className={`w-3 h-3 transition-transform ${showDetails ? 'rotate-90' : ''}`} />
            </button>
          ) : (
            <span className="text-xs text-slate-400 italic">Aucune activité récente</span>
          )}
        </div>
      </div>

      {/* Détail — RDV / activités / relances (accordéon) */}
      {showDetails && hasDetails && (
        <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {/* Prochains RDV */}
          <Section title="Prochains RDV (7 j.)" testid="upcoming-rdvs" empty={upcoming_rdvs.length === 0}>
            {upcoming_rdvs.slice(0, 4).map(r => (
              <RowButton key={r.prospect_id} onClick={() => onOpenProspect?.(r.prospect_id, r.category)}
                testid={`rdv-${r.prospect_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{r.nom_societe}</p>
                  <p className="text-[11px] text-slate-500 flex items-center gap-1">
                    {r.rdv_type === 'visio' ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                    {r.rdv_date}{r.rdv_heure ? ` · ${r.rdv_heure}` : ''}
                    {r.rr_relais ? ` · ${r.rr_relais}` : ''}
                  </p>
                </div>
              </RowButton>
            ))}
          </Section>

          {/* Activité récente */}
          <Section title="Activité récente" testid="recent-activities" empty={recent_activities.length === 0}>
            {recent_activities.slice(0, 4).map((a, i) => {
              const Icon = TYPE_ICONS[a.type] || StickyNote;
              const dt = a.date ? new Date(a.date) : null;
              return (
                <RowButton key={i} onClick={() => onOpenProspect?.(a.prospect_id, a.category)}>
                  <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-slate-500 truncate">
                      <span className="font-medium text-slate-800">{a.nom_societe}</span>
                      {dt && <> · {dt.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</>}
                    </p>
                    <p className="text-sm text-slate-700 truncate">{a.resume}</p>
                  </div>
                </RowButton>
              );
            })}
          </Section>

          {/* Relances dues */}
          <Section title="Relances en retard" testid="due-relances" empty={due_relances.length === 0}>
            {due_relances.slice(0, 4).map(r => (
              <RowButton key={r.prospect_id} onClick={() => onOpenProspect?.(r.prospect_id, r.category)}
                testid={`relance-${r.prospect_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{r.nom_societe}</p>
                  <p className="text-[11px] text-amber-700">{r.statut} · relance {r.next_relance_date}</p>
                </div>
              </RowButton>
            ))}
          </Section>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="flex items-center gap-2" data-testid={testid}>
      <Icon className={`w-4 h-4 ${tone}`} />
      <div>
        <span className="text-lg font-bold text-slate-900 tabular-nums leading-none">{value ?? 0}</span>
        <span className="text-[11px] text-slate-500 ml-1.5">{label}</span>
      </div>
    </div>
  );
}

function Section({ title, children, testid, empty }) {
  return (
    <div className="p-4" data-testid={testid}>
      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-2">{title}</p>
      {empty ? (
        <p className="text-xs text-slate-400 italic">—</p>
      ) : (
        <div className="space-y-1">{children}</div>
      )}
    </div>
  );
}

function RowButton({ onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className="w-full flex items-start gap-2 py-1 text-left group hover:bg-slate-50 -mx-2 px-2 rounded-md">
      {children}
      <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 mt-1 shrink-0" />
    </button>
  );
}
